import CircularProgress from '@mui/material/CircularProgress';
import config from '../../../constants/config';
import CountUp from 'react-countup';
import NormalButton from '../../../components/Buttons/NormalButton';
import React, { useRef } from 'react';
import SVG from 'src/components/Images/SvgRenderer';
import theme from 'src/ui/theme';
import useStyles from './style';
import { getAppData } from 'src/utils/useApp';
import { getQueryParams, getQueryString, isValidHexColor } from 'src/utils/useFunctions';
import { setLogoutUserController } from 'src/store/actions/controllers.actions';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { useEffect } from 'src/utils/useEffect';
import { useLocation, useNavigate } from 'react-router';
import { useStates } from 'src/utils/useState';
import { useTranslation } from 'react-i18next';

type LoadingScreenType = {
  customText?: any;
  status?: any;
};

const LoadingScreen: React.FunctionComponent<LoadingScreenType> = ({ customText, status }) => {

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loadingData = useAppSelector((state: any) => state.loading);
  const userData = useAppSelector((state: any) => state.user);
  const { t } = useTranslation();

  const queryParams = getQueryParams(location);
  const queryString = getQueryString(location);
  const embed = queryParams.get("embed");
  const isEmbed = embed === "true" ? true : false;
  const background = queryParams.get("background");
  const backgroundColor = isEmbed ? (background === "gradient" ? theme.colors.gradient : (isValidHexColor(`#${background}`) ? `#${background}` : theme.colors.white)) : theme.colors.white;

  const savedData = getAppData();
  const savedUsers = savedData.users ? savedData.users : [];

  const classes = useStyles({
    backgroundColor: backgroundColor,
    isEmbed: isEmbed,
  });

  const lastValue = useRef(0);

  const [state, setState] = useStates({
    start: 0,
    end: 0,
    wasLoggingOff: false,
  });
  
  const handleSwitch = () => {
    navigate(`/auth/switch${queryString}`);
  };

  const handleLogout = () => {
    dispatch(setLogoutUserController(true));
  };

  const handleClickTryAgain = () => {
    window.location.reload();
  };

  useEffect(() => {
    if(lastValue.current === 0) {
      setState("end", loadingData.userLoadedStatus);
      lastValue.current = loadingData.userLoadedStatus;
    } else {
      setState("start", state.end);
      setState("end", loadingData.userLoadedStatus);
    }
  }, [loadingData.userLoadedStatus, state, setState], [loadingData.userLoadedStatus]);

  useEffect(() => {
    if(userData.userStatus === "loggingOff") {
      setState("wasLoggingOff", true);
    }
    if(userData.userStatus === "loggedOff" && state.wasLoggingOff) {
      window.location.reload();
    }
  }, [userData.userStatus], [userData.userStatus]);

  return (
    <div className={classes.loadingScreen}>
      {
        !isEmbed ? (
          <SVG src={config.APP_LOGO}/>
        ) : null
      }
      {
        loadingData.isFailed ? (
          <div className={classes.failed}>
            {
              typeof loadingData.isFailed === "boolean" ? (
                <span>{t('failed_download_data')}!</span>
              ) : (
                <span>{t(`failed_${loadingData.isFailed}`)}</span>
              )
            }
            <div className={classes.buttons}>
              <NormalButton buttonType="primary" onClick={handleClickTryAgain}>
                {t("try_again")}
              </NormalButton>
              {
                (Object.keys(userData.userObject).length > 0 && !isEmbed) ? (
                  <>
                    {
                      savedUsers.length > 1 ? (
                        <NormalButton buttonType="secondary" onClick={handleSwitch}>
                          {t("switch_user")}
                        </NormalButton>
                      ) : (
                        <NormalButton buttonType="secondary" onClick={handleLogout}>
                          {t("logout")}
                        </NormalButton>
                      )
                    }
                  </>
                ) : null
              }
            </div>
          </div>        
        ) : (
          <div className={classes.loadingWrapper}>
            {}
            <div className={classes.loading}>
              {
                status === undefined || status === "loading" ? (
                  <CircularProgress className={classes.spinner}/>
                ) : status === "failed" ? (
                  <SVG src="failed"/>
                ) : null
              }
            </div>
            {
              customText ? (
                <span className={status ? status : "loading"}>
                  {customText}
                </span>
              ) : (
                <>
                  {
                    (!loadingData.isUserLoaded && userData.userStatus === "loggedIn") ? (
                      <span data-clarity-unmask="true">
                        <CountUp start={state.start} end={state.end} duration={0.5} suffix='%'/>
                      </span>
                    ) : null
                  }
                  {
                    ( userData.userStatus === "loggingOff") ? (
                      <span>{t('loggout_info')}</span>
                    ) : null
                  }
                </>
              )
            }
          </div>
        )
      }
      <div className={classes.footer}>
        <span data-clarity-unmask="true">
          &copy; {t('twigsee')} {new Date().getFullYear()}
        </span>
        {
          loadingData.isLanguageLoaded && !loadingData.isFailed ? (
            <>
              <span>|</span>
              <span data-clarity-unmask="true">{t('version')} {config.APP_VERSION}</span>
              {
                isEmbed ? (
                  <>
                    <span>|</span>
                    <span data-clarity-unmask="true">{t('version_embed')} {config.APP_EMBED_VERSION}</span>
                  </>
                ) : null
              }
            </>
          ) : null
        }
      </div>
    </div>
  );
}

export default LoadingScreen;